/**
 * SMSWiser Campaigns hooks (pin = handling)
 * For Media Devoted | Created By Ciro Autiero
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

export interface SmswiserCampaignRow {
  countryKey: string;
  wiserId: number;
  name: string;
  pinnedByMe: boolean;
  pinnedBy: Array<{ userId: string; email: string | null }>;
  handledBy: Array<{ userId: string; email: string | null }>;
}

export interface SmswiserCampaignsListResponse {
  total: number;
  rows: SmswiserCampaignRow[];
}

export interface SmswiserMyPinnedCampaign {
  countryKey: string;
  wiserId: number;
  name: string | null;
}

const DEFAULT_PAGE_SIZE = 500;

export interface SmswiserCampaignHandler {
  userId: string;
  email: string | null;
}

export const smswiserCampaignsKeys = {
  all: ['smswiser-campaigns'] as const,
  list: (params: {
    q?: string;
    countryKey?: string;
    pinnedOnly?: boolean;
    handledByUserIds?: string[];
    limit: number;
    offset: number;
  }) => [...smswiserCampaignsKeys.all, 'list', params] as const,
  handlers: () => [...smswiserCampaignsKeys.all, 'handlers'] as const,
  pins: () => [...smswiserCampaignsKeys.all, 'pins'] as const,
};

export function useSmswiserCampaignsHandlers() {
  return useQuery({
    queryKey: smswiserCampaignsKeys.handlers(),
    queryFn: async () => {
      const res = await api.get<{ data: SmswiserCampaignHandler[] }>('/smswiser/campaigns/handlers');
      return res.data.data;
    },
    staleTime: 60_000,
  });
}

export function useSmswiserCampaignsList(input: {
  q?: string;
  countryKey?: string;
  pinnedOnly?: boolean;
  handledByUserIds?: string[];
  limit?: number;
  offset: number;
  enabled?: boolean;
}) {
  const limit = input.limit ?? DEFAULT_PAGE_SIZE;
  const params = {
    q: input.q,
    countryKey: input.countryKey,
    pinnedOnly: input.pinnedOnly ?? false,
    handledByUserIds: input.handledByUserIds ?? undefined,
    limit,
    offset: input.offset,
  };

  const requestParams: Record<string, string | number | boolean> = {
    limit: params.limit,
    offset: params.offset,
  };
  if (params.pinnedOnly) requestParams.pinnedOnly = true;
  if (params.handledByUserIds?.length) {
    requestParams.handledByUserIds = params.handledByUserIds.join(',');
  }
  if (params.q != null && params.q !== '') requestParams.q = params.q;
  if (params.countryKey != null && params.countryKey !== '') requestParams.countryKey = params.countryKey;

  return useQuery({
    queryKey: smswiserCampaignsKeys.list(params),
    queryFn: async () => {
      const res = await api.get<{ data: SmswiserCampaignsListResponse }>('/smswiser/campaigns', {
        params: requestParams,
      });
      return res.data.data;
    },
    enabled: input.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useMySmswiserCampaignPins(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: smswiserCampaignsKeys.pins(),
    queryFn: async () => {
      const res = await api.get<{ data: SmswiserMyPinnedCampaign[] }>('/smswiser/campaigns/pins');
      return res.data.data;
    },
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });
}

export function usePinSmswiserCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { countryKey: string; wiserId: number }) => {
      const res = await api.post<{ data: { ok: boolean } }>('/smswiser/campaigns/pins', payload);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smswiserCampaignsKeys.all });
    },
  });
}

export function useUnpinSmswiserCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { countryKey: string; wiserId: number }) => {
      const res = await api.delete<{ data: { ok: boolean } }>('/smswiser/campaigns/pins', { data: payload });
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smswiserCampaignsKeys.all });
    },
  });
}

export function useClaimSmswiserCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { countryKey: string; wiserId: number }) => {
      const res = await api.post<{ data: { ok: boolean } }>('/smswiser/campaigns/claim', payload);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smswiserCampaignsKeys.all });
    },
  });
}

export function useReleaseSmswiserCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { countryKey: string; wiserId: number }) => {
      const res = await api.post<{ data: { ok: boolean } }>('/smswiser/campaigns/release', payload);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smswiserCampaignsKeys.all });
    },
  });
}

